import React from 'react';
import { useSelector } from 'react-redux';
import { ArcPolyline, DestinationMarker, TakeOffMarker } from '@src/core/controllers';
import { arcToMapCoordinate, arcWpsPath } from '@src/core/extensions';
import ZonesControl from '@src/core/features/zones/components/zones-control';
import { selectSelectedMissionTemplate } from '@src/features/order/pages/orders-page/redux';
import { splitHomeLeg } from '@src/features/order/pages/upsert-mission-page/utils';
import { RouteWaypointNumbers } from '@src/features/order/pages/upsert-mission-page/components';

const MissionTemplatesMap: React.FC = () => {
  const template = useSelector(selectSelectedMissionTemplate);

  const takeoff = template?.takeoffLocation;
  const destination = template?.destinationLocation;

  const takeoffPos = React.useMemo(
    () => ({
      lat: arcToMapCoordinate(takeoff?.latitude),
      lng: arcToMapCoordinate(takeoff?.longitude),
    }),
    [takeoff?.latitude, takeoff?.longitude],
  );

  const destinationPos = React.useMemo(
    () => ({
      lat: arcToMapCoordinate(destination?.latitude),
      lng: arcToMapCoordinate(destination?.longitude),
    }),
    [destination?.latitude, destination?.longitude],
  );

  const hasTakeoff = Number.isFinite(takeoffPos.lat) && Number.isFinite(takeoffPos.lng);
  const hasDestination = Number.isFinite(destinationPos.lat) && Number.isFinite(destinationPos.lng);

  const routePath = React.useMemo(() => {
    if (template?.waypoints?.length) {
      const { routeWaypoints } = splitHomeLeg(template.waypoints);
      const waypointPath = arcWpsPath(routeWaypoints);
      if (waypointPath.length >= 2) return waypointPath;
    }
    if (!hasTakeoff || !hasDestination) return [];
    return [takeoffPos, destinationPos];
  }, [template?.waypoints, hasTakeoff, hasDestination, takeoffPos, destinationPos]);

  if (!template) return <ZonesControl />;

  return (
    <React.Fragment>
      {routePath.length >= 2 && <ArcPolyline.default path={routePath} />}
      {hasTakeoff && <TakeOffMarker.confirmed position={takeoffPos} />}
      {hasDestination && <DestinationMarker.confirmed position={destinationPos} />}
      <RouteWaypointNumbers
        waypoints={template?.waypoints}
        takeoff={hasTakeoff ? takeoffPos : null}
        destination={hasDestination ? destinationPos : null}
      />
      <ZonesControl />
    </React.Fragment>
  );
};

export default MissionTemplatesMap;
